import './chart.scss';
import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const Chart = ({ aspect, year = new Date().getFullYear() }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Helper to extract merchantId from JWT
  const getMerchantIdFromToken = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload).merchantId;
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  };

  useEffect(() => {
    const fetchMonthlySales = async () => {
      setLoading(true);
      setError(null);
      try {
        const merchantId = getMerchantIdFromToken();
        if (!merchantId) throw new Error('Merchant ID not found');

        // Use the correct endpoint: /year
        const response = await fetch(
          `${apiUrl}/orders/year?merchantId=${merchantId}&year=${year}`
        );
        if (!response.ok) throw new Error('Failed to fetch sales data');

        const result = await response.json(); // [{ month, monthName, value }]
        // Transform to chart format: { name: monthName, Total: value }
        const chartData = result.map((item) => ({
          name: item.monthName,
          Total: item.value,
        }));
        setData(chartData);
      } catch (err) {
        console.error('Error fetching monthly sales:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlySales();
  }, [apiUrl, year]); // Refetch if year changes

  if (loading) return <div className="chart">Loading sales data...</div>;
  if (error) return <div className="chart">Error: {error}</div>;
  if (data.length === 0) return <div className="chart">No sales data available for {year}</div>;

  return (
    <div className="chart">
      <div className="title">Last 12 Months Sales ({year})</div>
      <ResponsiveContainer width="100%" aspect={aspect}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="total" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" stroke="gray" />
          <CartesianGrid strokeDasharray="3" className="chartGrid" />
          <Tooltip />
          <Area
            type="monotone"
            dataKey="Total"
            stroke="#8884d8"
            fillOpacity={1}
            fill="url(#total)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;